<script lang="ts" setup>
import type { CSSProperties, PropType} from 'vue';
import { computed, onMounted, ref, onUnmounted } from 'vue';
import type { ICellRendererParams } from 'ag-grid-community';
import type { BaseGridContext } from '../types';

type ExtendedParams = ICellRendererParams<unknown, unknown, BaseGridContext | undefined> & {
    id: string;
    title?: ((params: ICellRendererParams) => string) | string | number | null;
    icon: (params: ICellRendererParams) => string;
    wrap: (params: ICellRendererParams) => boolean;
    tooltip: (params: ICellRendererParams) => boolean;
    readonly: ((params: ICellRendererParams) => boolean) | boolean;
    textStyle: ((params: ICellRendererParams) => CSSProperties) | CSSProperties;
    warningMessage?: ((params: ICellRendererParams) => string | null) | string | null;
    alwaysShowIcon?: ((params: ICellRendererParams) => boolean) | boolean;
    noPadding?: boolean;
    dataTest?: string;
};

const props = defineProps({
    params: {
        type: Object as PropType<ExtendedParams>,
        required: true
    }
});

const title = ref<string>('status');
const icon = ref<string>('check');
const readonly = ref<boolean>(false);
const wrap = ref(false);
const textStyle = ref<CSSProperties>({});
const tooltip = ref(true);
const warningMessage = ref<string | null>(null);
const alwaysShowIcon = ref<boolean>(false);

const isEditing = ref(false);
const cellKey = computed(() => `${props.params.node.id ?? ''}:${props.params.column?.getColId()}`);
const errorContent = computed(() => props.params.context?.cellState?.get(cellKey.value)?.message ?? null);

function onCellEditingStarted(event: { node: { id: string | undefined }; colDef?: { field?: string } }): void {
    if (event.node.id === props.params.node.id && event.colDef?.field === props.params.colDef?.field) {
        isEditing.value = true;
    }
}

function onCellEditingStopped(): void {
    isEditing.value = false;
}

// Falls back to the cell's formatted value (or raw value) when no explicit title is passed, so
// columns with a valueFormatter don't need to wire `title` manually — the renderer reads it from params.
function resolveTitle(params: ExtendedParams): string {
    if (params.title != null) {
        return typeof params.title === 'function' ? params.title(params) : String(params.title);
    }
    return params.valueFormatted ?? (params.value != null ? String(params.value) : '');
}

onMounted(() => {
    props.params.api.addEventListener('cellEditingStarted', onCellEditingStarted);
    props.params.api.addEventListener('cellEditingStopped', onCellEditingStopped);
    title.value = resolveTitle(props.params);
    icon.value = typeof props.params.icon === 'function' ? props.params.icon(props.params) : props.params.icon;
    readonly.value = typeof props.params.readonly === 'function' ? props.params.readonly(props.params) : props.params.readonly;
    wrap.value = typeof props.params.wrap === 'function' ? props.params.wrap(props.params) : props.params.wrap;
    textStyle.value = typeof props.params.textStyle === 'function' ? props.params.textStyle(props.params) : props.params.textStyle;
    warningMessage.value = typeof props.params.warningMessage === 'function' ? props.params.warningMessage(props.params) : props.params.warningMessage ?? null;
    alwaysShowIcon.value = typeof props.params.alwaysShowIcon === 'function' ? props.params.alwaysShowIcon(props.params) : props.params.alwaysShowIcon ?? false;

    const tooltipPropVal = typeof props.params.tooltip === 'function' ? props.params.tooltip(props.params) : props.params.tooltip;
    tooltip.value = tooltipPropVal ?? true;
});

onUnmounted(() => {
    if (props.params.api.isDestroyed()) return;
    props.params.api.removeEventListener('cellEditingStarted', onCellEditingStarted);
    props.params.api.removeEventListener('cellEditingStopped', onCellEditingStopped);
});

</script>

<template>
    <div
        v-tooltip="{ content: errorContent ?? undefined, shown: errorContent !== null, triggers: [], theme: 'dark-tooltip' }"
        :class="['sy-edit-field sy-pointer', { 'sy-default': readonly, 'editing': isEditing, 'no-padding': params.noPadding }]"
        :data-test="params.dataTest"
    >
        <span
            v-if="tooltip"
            v-tooltip="title"
            :style="textStyle"
            :class="[wrap ? 'sy-pre-wrap sy-leading-3' : 'sy-truncate']"
        >{{ title }}</span>
        <span
            v-else
            :style="textStyle"
            :class="[wrap ? 'sy-pre-wrap sy-leading-3' : 'sy-truncate']"
        >{{ title }}</span>
        <div v-if="!readonly" class="flex-container align-middle">
            <Icon
                v-tooltip="warningMessage ?? undefined"
                :icon="icon"
                :class="{ 'sy-edit-field-icon': !alwaysShowIcon, 'sy-edit-field-icon--warning': warningMessage != null }"
            />
        </div>
    </div>
</template>
<style lang="scss" scoped>
    .ag-cell {
        &:hover {
            .sy-edit-field-icon {
                display: block;
            }
        }
    }
    .sy-edit-field {
        display: flex;
        height: 100%;
        padding: 0 var(--sy-spacer-8);
        align-items: center;
        justify-content: space-between;

        .sy-edit-field-icon {
            display: none;
        }

        .sy-edit-field-icon--warning {
            color: var(--sy-orange-500);
        }

        &.editing {
            background-color: var(--sy-blue-100);
            color: var(--sy-blue-600);
        }

        &.no-padding {
            padding: 0;
        }
    }
</style>
